import axios from 'axios';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getEmployeeFunctionLinksRequest,
  getLinkCountsByFunctionRequest,
  getSelectableFunctionsRequest,
  linkFunctionsToEmployeesRequest,
  replaceLinkedFunctionsRequest,
  unlinkAllFunctionsRequest,
} from 'api/requests/employee-function-link-requests';
import {
  EMPLOYEE_FUNCTION_LINKS__ADD,
  EMPLOYEE_FUNCTION_LINKS__ADD_ERROR,
  EMPLOYEE_FUNCTION_LINKS__ADD_SUCCESS,
  EMPLOYEE_FUNCTION_LINKS__CLEAR_ERRORS,
  EMPLOYEE_FUNCTION_LINKS__DELETE,
  EMPLOYEE_FUNCTION_LINKS__DELETE_ERROR,
  EMPLOYEE_FUNCTION_LINKS__DELETE_SUCCESS,
  EMPLOYEE_FUNCTION_LINKS__GET,
  EMPLOYEE_FUNCTION_LINKS__GET_COUNTS,
  EMPLOYEE_FUNCTION_LINKS__GET_SELECTABLE,
  EMPLOYEE_FUNCTION_LINKS__SET,
  EMPLOYEE_FUNCTION_LINKS__SET_COUNTS,
  EMPLOYEE_FUNCTION_LINKS__SET_ERROR,
  EMPLOYEE_FUNCTION_LINKS__SET_LOADING,
  EMPLOYEE_FUNCTION_LINKS__SET_SELECTABLE,
  EMPLOYEE_FUNCTION_LINKS__UPDATE,
  EMPLOYEE_FUNCTION_LINKS__UPDATE_ERROR,
  EMPLOYEE_FUNCTION_LINKS__UPDATE_SUCCESS,
} from 'store/constants';
import IEmployeeFunctionLink from 'types/company/payroll/IEmployeeFunctionLink';
import ISelectableFunctions from 'types/company/payroll/ISelectableFunctions';
import logError from 'utils/log-error';

export const setEmployeeFunctionLinksLoadingAction = createAction<boolean>(EMPLOYEE_FUNCTION_LINKS__SET_LOADING);
export const setEmployeeFunctionLinksAction = createAction<IEmployeeFunctionLink[]>(EMPLOYEE_FUNCTION_LINKS__SET);
export const setEmployeeFunctionLinksErrorAction = createAction<boolean>(EMPLOYEE_FUNCTION_LINKS__SET_ERROR);
export const setLinkCountsByFunctionAction = createAction<Record<number, number>>(EMPLOYEE_FUNCTION_LINKS__SET_COUNTS);
export const setSelectableFunctionsAction = createAction<ISelectableFunctions>(EMPLOYEE_FUNCTION_LINKS__SET_SELECTABLE);

export const addEmployeeFunctionLinkSuccessAction = createAction<boolean>(EMPLOYEE_FUNCTION_LINKS__ADD_SUCCESS);
export const addEmployeeFunctionLinkErrorAction = createAction<string>(EMPLOYEE_FUNCTION_LINKS__ADD_ERROR);
export const updateEmployeeFunctionLinkSuccessAction = createAction<boolean>(EMPLOYEE_FUNCTION_LINKS__UPDATE_SUCCESS);
export const updateEmployeeFunctionLinkErrorAction = createAction<string>(EMPLOYEE_FUNCTION_LINKS__UPDATE_ERROR);
export const deleteEmployeeFunctionLinkSuccessAction = createAction<boolean>(EMPLOYEE_FUNCTION_LINKS__DELETE_SUCCESS);
export const deleteEmployeeFunctionLinkErrorAction = createAction<string>(EMPLOYEE_FUNCTION_LINKS__DELETE_ERROR);
export const clearEmployeeFunctionLinkErrorsAction = createAction<void>(EMPLOYEE_FUNCTION_LINKS__CLEAR_ERRORS);

const rejectedKey = (error: unknown): string =>
  axios.isAxiosError(error) && error.response && typeof error.response.data === 'string' ? error.response.data : '';

export const getEmployeeFunctionLinksAction = createAsyncThunk(
  EMPLOYEE_FUNCTION_LINKS__GET,
  async (companyId: number, thunkApi) => {
    thunkApi.dispatch(setEmployeeFunctionLinksLoadingAction(true));
    thunkApi.dispatch(setEmployeeFunctionLinksErrorAction(false));
    try {
      const response = await getEmployeeFunctionLinksRequest(companyId);
      thunkApi.dispatch(setEmployeeFunctionLinksAction(response));
    } catch (error) {
      logError(error);
      thunkApi.dispatch(setEmployeeFunctionLinksErrorAction(true));
    }
    thunkApi.dispatch(setEmployeeFunctionLinksLoadingAction(false));
  },
);

export const linkFunctionsToEmployeesAction = createAsyncThunk(
  EMPLOYEE_FUNCTION_LINKS__ADD,
  async (props: { companyId: number; employeeIds: number[]; functionIds: number[] }, thunkApi) => {
    thunkApi.dispatch(setEmployeeFunctionLinksLoadingAction(true));
    thunkApi.dispatch(addEmployeeFunctionLinkErrorAction(''));
    thunkApi.dispatch(addEmployeeFunctionLinkSuccessAction(false));
    try {
      await linkFunctionsToEmployeesRequest(props);
      thunkApi.dispatch(addEmployeeFunctionLinkSuccessAction(true));
      thunkApi.dispatch(getEmployeeFunctionLinksAction(props.companyId));
    } catch (error) {
      logError(error);
      thunkApi.dispatch(addEmployeeFunctionLinkErrorAction(rejectedKey(error)));
    }
    thunkApi.dispatch(setEmployeeFunctionLinksLoadingAction(false));
  },
);

export const replaceLinkedFunctionsAction = createAsyncThunk(
  EMPLOYEE_FUNCTION_LINKS__UPDATE,
  async (props: { companyId: number; employeeId: number; functionIds: number[] }, thunkApi) => {
    thunkApi.dispatch(setEmployeeFunctionLinksLoadingAction(true));
    thunkApi.dispatch(updateEmployeeFunctionLinkErrorAction(''));
    thunkApi.dispatch(updateEmployeeFunctionLinkSuccessAction(false));
    try {
      await replaceLinkedFunctionsRequest(props);
      thunkApi.dispatch(updateEmployeeFunctionLinkSuccessAction(true));
      thunkApi.dispatch(getEmployeeFunctionLinksAction(props.companyId));
    } catch (error) {
      logError(error);
      thunkApi.dispatch(updateEmployeeFunctionLinkErrorAction(rejectedKey(error)));
    }
    thunkApi.dispatch(setEmployeeFunctionLinksLoadingAction(false));
  },
);

export const unlinkAllFunctionsAction = createAsyncThunk(
  EMPLOYEE_FUNCTION_LINKS__DELETE,
  async (props: { companyId: number; employeeId: number }, thunkApi) => {
    thunkApi.dispatch(setEmployeeFunctionLinksLoadingAction(true));
    thunkApi.dispatch(deleteEmployeeFunctionLinkErrorAction(''));
    thunkApi.dispatch(deleteEmployeeFunctionLinkSuccessAction(false));
    try {
      await unlinkAllFunctionsRequest(props);
      thunkApi.dispatch(deleteEmployeeFunctionLinkSuccessAction(true));
      thunkApi.dispatch(getEmployeeFunctionLinksAction(props.companyId));
    } catch (error) {
      logError(error);
      thunkApi.dispatch(deleteEmployeeFunctionLinkErrorAction(rejectedKey(error)));
    }
    thunkApi.dispatch(setEmployeeFunctionLinksLoadingAction(false));
  },
);

export const getLinkCountsByFunctionAction = createAsyncThunk(
  EMPLOYEE_FUNCTION_LINKS__GET_COUNTS,
  async (props: { companyId: number; functionIds: number[] }, thunkApi) => {
    try {
      const response = await getLinkCountsByFunctionRequest(props);
      thunkApi.dispatch(setLinkCountsByFunctionAction(response));
      return response;
    } catch (error) {
      logError(error);
      thunkApi.dispatch(setLinkCountsByFunctionAction({}));
      return {} as Record<number, number>;
    }
  },
);

export const getSelectableFunctionsAction = createAsyncThunk(
  EMPLOYEE_FUNCTION_LINKS__GET_SELECTABLE,
  async (props: { companyId: number; employeeIds: number[] }, thunkApi) => {
    try {
      const response = await getSelectableFunctionsRequest(props);
      thunkApi.dispatch(setSelectableFunctionsAction(response));
      return response;
    } catch (error) {
      logError(error);
      return { perEmployee: [], commonFunctionIds: [] } as ISelectableFunctions;
    }
  },
);
